import { createStore, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { persistStore, persistReducer } from 'redux-persist';
import logger from 'redux-logger';
import { composeWithDevTools } from 'redux-devtools-extension';
import rootState from '../redux/root.reducer';
import storage from 'redux-persist/lib/storage';
import rootSaga from './root.saga';
import { migrateBasemapId } from 'utilities/basemap-migration.util';
import { debugLog } from 'utilities/basemap-debug.util';
import { MAP_DEFAULT_VAR } from 'types';

/**
 * Migration function for redux-persist
 * Handles backward compatibility for basemap ID changes
 */
const migrations: { [key: number]: (state: any) => any } = {
  // Migration version 1: Convert webmap IDs to config keys
  1: function (state: any) {
    if (state && state.app && state.app.selectedBasemap) {
      var migratedBasemap = migrateBasemapId(state.app.selectedBasemap);
      if (migratedBasemap !== state.app.selectedBasemap) {
        debugLog(
          '[Redux Persist Migration] Migrated selectedBasemap:',
          state.app.selectedBasemap,
          '→',
          migratedBasemap
        );
      }
      return {
        ...state,
        app: {
          ...state.app,
          selectedBasemap: migratedBasemap
        }
      };
    }
    return state;
  },
  // Migration version 2: Ensure selectedBasemap exists for users with missing state
  2: function (state: any) {
    if (state && state.app && !state.app.selectedBasemap) {
      debugLog(
        '[Redux Persist Migration v2] Adding missing selectedBasemap:',
        MAP_DEFAULT_VAR.BASEMAP
      );
      return {
        ...state,
        app: {
          ...state.app,
          selectedBasemap: MAP_DEFAULT_VAR.BASEMAP
        }
      };
    }
    return state;
  }
};

const persistConfig = {
  key: 'root',
  version: 2,
  whitelist: ['app', 'session', 'members'],
  blacklist: [],
  storage,
  migrate: function (state: any, currentVersion: number) {
    // Run migrations sequentially from stored version to current
    var migratedState = state;

    // If no version, assume version 0 (legacy)
    var fromVersion = state?._persist?.version || 0;

    debugLog(
      '[Redux Persist] Running migrations from version',
      fromVersion,
      'to',
      currentVersion
    );

    // Apply all migrations from fromVersion to currentVersion
    for (var version = fromVersion + 1; version <= currentVersion; version++) {
      var migrationFn = migrations[version];
      if (migrationFn) {
        debugLog('[Redux Persist] Applying migration v' + version);
        migratedState = migrationFn(migratedState);
      }
    }

    return Promise.resolve(migratedState);
  }
};
const persistedReducer = persistReducer(persistConfig, rootState);
const sagaMiddleware = createSagaMiddleware();

export const configureStore = createStore(
  persistedReducer,
  composeWithDevTools(
    process.env.REACT_APP_REDUX_LOGGER === 'true'
      ? applyMiddleware(logger, sagaMiddleware)
      : applyMiddleware(sagaMiddleware)
  )
);

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(configureStore);

export const createTestStore = (initializeState: any) => {
  return createStore(rootState, initializeState);
};

export default { configureStore, persistor, createStore };
